import {AfterViewInit, Component, ElementRef, Inject, OnInit, QueryList, ViewChildren} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../store';
import {StudentEnrollmentWizardSpriteComponent} from './student-enrollment-wizard.sprite.component';
import {EnrolmentField, StudentEnrollmentSettings} from '../../../../models/student-enrollment-settings.model';
import {NgStyle} from '@angular/common';
import {MatFormField} from '@angular/material/form-field';
import {MatOption} from '@angular/material/autocomplete';
import {MatSelect, MatSelectChange, MatSelectTrigger} from '@angular/material/select';
import {HttpClient} from '@angular/common/http';
import {SchoolUsersDbService} from '../../../../services/school-users-db.service';
import {selectUser} from '../../../../store/selectors';
import {combineLatest, concatMap} from 'rxjs';
import {TenantService} from '../../../../services/tenant.service';
import {userDetailsUpdated} from '../../../../store/user.actions';
import {LoadingService} from '../../../../shared/services/loading.service';
import {MessagesService} from '../../../../shared/services/messages.service';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'student-enrollment-wizard',
    imports: [
    StudentEnrollmentWizardSpriteComponent,
    ReactiveFormsModule,
    NgStyle,
    MatFormField,
    MatOption,
    MatSelect,
    MatSelectTrigger,
    TranslateModule
],
    templateUrl: './student-enrollment-wizard.component.html',
    styleUrl: './student-enrollment-wizard.component.scss'
})
export class StudentEnrollmentWizardComponent implements OnInit, AfterViewInit {

  studentEnrolmentSettings: StudentEnrollmentSettings;

  current: string | number

  form = this.fb.group({})
  private jsonUrl = 'assets/countries/countries.json';
  countries: string[];
  private user: any;
  preview

  @ViewChildren('enrollmentOpenField') enrollmentOpenField!: QueryList<ElementRef>;
  @ViewChildren('inputField') inputField!: QueryList<ElementRef>;

  constructor(@Inject(MAT_DIALOG_DATA) data: { studentEnrolmentSettings: StudentEnrollmentSettings, preview: boolean },
              private dialogRef: MatDialogRef<StudentEnrollmentWizardComponent>,
              private fb: FormBuilder,
              private store: Store<AppState>,
              private http: HttpClient,
              private usersDBService: SchoolUsersDbService,
              private tenant: TenantService,
              private loadingService: LoadingService,
              private messages: MessagesService
  ) {

    this.preview = data?.preview;

    this.studentEnrolmentSettings = {
      ...data?.studentEnrolmentSettings,
      enrollmentFields: data?.studentEnrolmentSettings.enrollmentFields.filter((enroll) => enroll?.enabled)
    };

    const showStartScreen = this.studentEnrolmentSettings?.enrollmentSequence?.showStartScreen || false

    if (showStartScreen) {
      this.current = "first"
    } else {
      this.current = 0;
    }

  }

  ngAfterViewInit() {

    this.enrollmentOpenField?.changes?.subscribe((value) => {
      this.scrollToBottom();
    })

    this.inputField?.changes.subscribe((value) => {
      this.inputField?.last?.nativeElement?.focus();
    })

  }

  ngOnInit() {

    this.getCountries();

    combineLatest([
      this.store.select(selectUser),
    ]).pipe(
      concatMap(([user]) => {

        this.user = user;

        return []

      })
    ).subscribe()

    this.studentEnrolmentSettings?.enrollmentFields?.forEach((enrollmentField) => {

      if(enrollmentField?.enabled) {

        const validators = []

        if(enrollmentField?.mandatory) {
          validators.push(Validators.required)
        }

        if (enrollmentField?.fieldId === 'email') {

          validators.push(Validators.email)

        }

        if (enrollmentField?.fieldId === 'phoneNumber') {

          validators.push(Validators.pattern(/^\+?[0-9\s\-().]{7,20}$/))

        }

        this.form.addControl(
          enrollmentField?.fieldName,
          new FormControl(null, validators)
        );

      }

    });

  }

  next() {

      const valid = this.isCurrentControlValid()

      if(!valid) {
        return;
      }

      if ((+this.current + 1) > this.studentEnrolmentSettings.enrollmentFields.length - 1) {
        this.current = "last"
      } else {

        this.current = (+this.current) + 1;

      }

  }

  previous() {

    if (((+this.current) - 1) >= 0) {

      this.current = (+this.current) - 1

    }

  }

  yes() {
    this.current = 0
  }

  close() {
    this.dialogRef.close();
  }

  openClosed(index: number) {
    this.current = index
  }

  complete() {

    const valid = this.isCurrentControlValid()

    if(!valid) {
      return;
    }

    if(this.preview) {

      if (this.studentEnrolmentSettings?.enrollmentSequence?.showEndScreen) {
        this.current = "last"
      } else {
        this.close()
      }

      return;
    }

    this.loadingService.loadingOn();

    this.usersDBService.updateUserByTenantAndId(this.tenant.id, this.user.id, {
      studentEnrollment: {
        ...this.form.value
      }
    }).subscribe({
        next: () => {

          this.loadingService.loadingOff()

          this.store.dispatch(
            userDetailsUpdated({
              userId: this.user.id,
              changes: {
                ...this.form.value
              }
            })
          );

          if (this.studentEnrolmentSettings?.enrollmentSequence?.showEndScreen) {
            this.current = "last"
          } else {
            this.close()
          }

      },
      error: error => {
        this.loadingService.loadingOff();
        this.messages.error("Error loading student enrollment WizardComponent", error)
      }
    });

  }

  onSelectionChange($event: MatSelectChange) {

    const country = this.studentEnrolmentSettings.enrollmentFields.find(enrol => enrol?.fieldId === "country");

    this.form.patchValue({
      [country?.fieldName]: $event?.value,
    });

  }

  getCountries() {
    console.log('getCountries',this.constructDomain() + this.jsonUrl)
    return this.http.get<any>(this.constructDomain() + this.jsonUrl).subscribe((countries => {

      this.countries = countries?.map(item => item?.country);

    }));
  }

  isControlInvalid(enrollmentField: EnrolmentField): boolean {
    const control = this.form.get(enrollmentField?.fieldName);
    return enrollmentField?.mandatory && control?.touched && control?.hasError('required');
  }

  invalidPhone(enrollmentField: EnrolmentField): boolean {
    const control = this.form.get(enrollmentField?.fieldName);
    return control?.hasError('pattern') && control?.touched;
  }


  border(enrollmentField: EnrolmentField) {

    if(enrollmentField?.mandatory) {

      const currentControl = this.form.get(enrollmentField?.fieldName)

      if (currentControl?.touched && currentControl?.invalid) {
        return "1px solid red"
      }

    }

    return 'initial'

  }

  isCurrentControlValid() {

    // get control from index
    const enrollmentField = this.studentEnrolmentSettings.enrollmentFields[this.current];

    if(this.form.get(enrollmentField?.fieldName).hasError('email')) {
      return false
    }

    if(enrollmentField?.mandatory) {

      const currentControl = this.form.get(enrollmentField?.fieldName)
      currentControl.markAsTouched()

      if (currentControl?.invalid) {
        return false
      }

    }

    return true

  }

  scrollToBottom(): void {

    this.enrollmentOpenField?.last?.nativeElement.scrollIntoView({})

  }

  isNum() {

    return Number.isFinite(this.current);

  }

  protected readonly Number = Number;

  keyup($event: KeyboardEvent) {

    if ($event.code === 'Enter') {
      if(this.current === (this.studentEnrolmentSettings?.enrollmentFields?.length - 1)){
        this.complete();
      } else {
        this.next();
      }
    }

  }

  inputType(enrollmentField: EnrolmentField) {

    // if(enrollmentField?.fieldId === 'postalCode') {
    //   return 'number'
    // }

    if(enrollmentField?.fieldId === 'phoneNumber') {
      return 'tel'
    }

    if (enrollmentField?.fieldId === 'email') {
      return 'email'
    }

    return 'text'

  }

  invalidEmail(enrollmentField) {
    return this.form.get(enrollmentField?.fieldName)?.hasError('email') && this.form.get(enrollmentField?.fieldName)?.touched
  }

  private constructDomain() {

    const tenant = this.tenant?.tenant;

    const WEBSITE_PROTOCOL = window?.location?.protocol;
    const ORIGIN = window?.location?.origin

    const WEBSITE_DOMAIN = 'onlinecoursehost.com/';

    const url = `${WEBSITE_PROTOCOL}//`;

    if (tenant?.customDomain) {

      if (WEBSITE_PROTOCOL === 'http:') {
        return ORIGIN + '/';
      }

      return url + tenant?.subDomain + '.' + WEBSITE_DOMAIN;

    } else {

      return ORIGIN + '/';

    }

  }

}
